import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Box, Typography, useTheme } from "@mui/material";

const pageVariants = {
  initial: (direction) => ({
    x: direction > 0 ? 300 : -300,
    opacity: 0,
    scale: 0.95,
  }),
  animate: {
    x: 0,
    opacity: 1,
    scale: 1,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 30,
      opacity: { duration: 0.2 },
    },
  },
  exit: (direction) => ({
    x: direction > 0 ? -300 : 300,
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.2,
    },
  }),
};

const contentVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 300,
      damping: 24,
    },
  },
};

const WizardStep = ({
  stepKey,
  direction,
  title,
  subtitle,
  icon: Icon,
  children,
}) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  const colors = {
    cardBg: isDarkMode ? "#1e293b" : "#ffffff",
    cardBorder: isDarkMode ? "#334155" : "#e2e8f0",
    titleColor: isDarkMode ? "#f1f5f9" : "#1e293b",
    subtitleColor: isDarkMode ? "#94a3b8" : "#64748b",
    iconBg: isDarkMode
      ? "linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)"
      : "linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)",
    iconColor: isDarkMode ? "#FB923C" : "#F97316",
  };

  return (
    <AnimatePresence mode="wait" custom={direction}>
      <Box
        component={motion.div}
        key={stepKey}
        custom={direction}
        variants={pageVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        sx={{
          width: "100%",
          minHeight: "400px",
        }}
      >
        <motion.div variants={contentVariants} initial="hidden" animate="visible">
          {/* Step Header */}
          {(title || subtitle || Icon) && (
            <Box
              component={motion.div}
              variants={itemVariants}
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                mb: 3,
                pb: 2,
                borderBottom: `1px solid ${colors.cardBorder}`,
              }}
            >
              {Icon && (
                <Box
                  sx={{
                    width: 48,
                    height: 48,
                    borderRadius: "12px",
                    background: colors.iconBg,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Icon sx={{ color: colors.iconColor, fontSize: 24 }} />
                </Box>
              )}
              <Box>
                {title && (
                  <Typography
                    variant="h6"
                    sx={{
                      color: colors.titleColor,
                      fontWeight: 600,
                      fontSize: "1.1rem",
                    }}
                  >
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography
                    variant="body2"
                    sx={{
                      color: colors.subtitleColor,
                      fontSize: "0.875rem",
                    }}
                  >
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
          )}

          {/* Step Content */}
          <Box
            component={motion.div}
            variants={itemVariants}
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 2.5,
            }}
          >
            {children}
          </Box>
        </motion.div>
      </Box>
    </AnimatePresence>
  );
};

// Animated form field wrapper
export const AnimatedField = ({ children, delay = 0 }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 15 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{
        delay,
        type: "spring",
        stiffness: 300,
        damping: 24,
      }}
    >
      {children}
    </motion.div>
  );
};

// Section divider with label
export const SectionDivider = ({ label }) => {
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === "dark";

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, scaleX: 0 }}
      animate={{ opacity: 1, scaleX: 1 }}
      transition={{ duration: 0.3 }}
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        my: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: "1px",
          background: isDarkMode
            ? "linear-gradient(90deg, transparent, #334155)"
            : "linear-gradient(90deg, transparent, #e2e8f0)",
        }}
      />
      <Typography
        sx={{
          fontSize: "0.75rem",
          fontWeight: 500,
          color: isDarkMode ? "#64748b" : "#94a3b8",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
        }}
      >
        {label}
      </Typography>
      <Box
        sx={{
          flex: 1,
          height: "1px",
          background: isDarkMode
            ? "linear-gradient(90deg, #334155, transparent)"
            : "linear-gradient(90deg, #e2e8f0, transparent)",
        }}
      />
    </Box>
  );
};

export default WizardStep;
